import { format } from 'date-fns';
import { id as idLocale } from 'date-fns/locale';

/**
 * Formats image URL to full path.
 * Handles URLs with or without leading slash to avoid double slashes.
 * 
 * @param imageUrl - Relative or absolute image URL
 * @returns Full URL to the image
 */
export const getFullImageUrl = (imageUrl: string): string => {
  if (!imageUrl) return '';
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }
  
  const cleanUrl = imageUrl.startsWith('/') ? imageUrl.slice(1) : imageUrl;
  
  // Differentiate between Sakti BCC (/uploads/) and Mutu Buah (/images/)
  if (cleanUrl.startsWith('uploads/')) {
    // Sakti BCC images reside on the Sakti server
    return `https://sakti.gbs.id/${cleanUrl}`;
  } else {
    // Mutu Buah images reside on the Insight API production server (due to DB clone)
    return `https://insight-api.gbs.id/${cleanUrl}`;
  }
};

/**
 * Formats quantity values. Displays "-" if value is 0 or equivalent.
 */
export const formatQty = (value: number | string | undefined | null): string => {
  if (value === undefined || value === null) return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (num === 0 || isNaN(num)) return '-';
  return String(value);
};

/**
 * Formats discrepancy values to be always positive (absolute value)
 * and returns "-" if the value is 0.
 */
export const formatDiscrepancy = (value: number | string | undefined | null): string => {
  if (value === undefined || value === null) return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num) || num === 0) return '-';
  return String(Math.abs(num));
};

/**
 * Formats ISO date-time string (e.g. "2026-06-29T16:45:00" or "2026-06-29T16:45:00Z")
 * to Indonesian formatted string (e.g. "29 Juni 2026, 16:45").
 * Uses date-fns to match Feed module's formatting style.
 * Trims 'Z' to prevent local timezone shift by the browser.
 */
export const formatIndonesianDateTime = (dateTimeStr?: string): string => {
  if (!dateTimeStr) return '-';
  try {
    const cleanStr = dateTimeStr.endsWith('Z') ? dateTimeStr.slice(0, -1) : dateTimeStr;
    const dateObj = new Date(cleanStr);
    if (isNaN(dateObj.getTime())) return '-';
    return format(dateObj, 'dd MMMM yyyy, HH:mm', { locale: idLocale });
  } catch (e) {
    return '-';
  }
};
